// SSE client for Agent Chat (Phase 3). We can't use EventSource — it can't POST or send
// the Bearer header — so we POST with fetch and parse the response body stream ourselves.
//
// A real frame parser is required: network chunks don't align with SSE frames or even UTF-8
// boundaries, so we decode incrementally (TextDecoder stream mode) and split on blank lines.
// Lines beginning with ':' are comments/heartbeats and carry no data.

import { AgentChatStreamEvent } from './types';

interface StreamArgs {
  url: string;
  headers: Record<string, string>;
  message: string;
  signal: AbortSignal;
  onEvent: (ev: AgentChatStreamEvent) => void;
}

// Thrown for failures that happen BEFORE the stream opens (HTTP non-2xx with a JSON body —
// e.g. 409 turn-in-progress, 429 budget, 400 validation). `handled` marks that the message
// is already user-facing.
export class PreStreamError extends Error {
  handled = true;
  constructor(message: string) {
    super(message);
    this.name = 'PreStreamError';
  }
}

export async function streamAgentMessage({ url, headers, message, signal, onEvent }: StreamArgs): Promise<void> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...headers },
    body: JSON.stringify({ message }),
    signal
  });

  if (!res.ok || !res.body) {
    let msg = 'Send failed. Please try again.';
    try {
      const data = await res.json();
      if (data && data.error) msg = data.error;
    } catch {
      /* non-JSON error body — keep the default */
    }
    throw new PreStreamError(msg);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    // Normalize CRLF so frame/line splitting is consistent regardless of proxy rewriting.
    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');

    let sep: number;
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const dataLines = frame
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).replace(/^ /, ''));
      if (dataLines.length === 0) continue; // comment / heartbeat
      try {
        onEvent(JSON.parse(dataLines.join('\n')) as AgentChatStreamEvent);
      } catch {
        /* skip a malformed frame rather than killing the whole stream */
      }
    }
  }
}
